import { useCallback, useEffect, useState } from 'react';

// import dayjs from 'dayjs';

import dayjs from 'dayjs';

import {
  IStoredPeriod,
  IUseSharedDateRange,
  PRESELECTED_RANGE,
} from './interfaces';

const STORAGE_KEY = 'report_date_range';
const CUSTOM_EVENT_NAME = 'report_date_range:changed';

const DEFAULT: IStoredPeriod = {
  startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
  endDate: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  periodType: 'month',
  offset: 0,
};
function readStorage(): IStoredPeriod {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    return JSON.parse(raw) as IStoredPeriod;
  } catch {
    return DEFAULT;
  }
}

export const useSharedDateRange = (): IUseSharedDateRange => {
  const [data, setData] = useState<IStoredPeriod>(() => readStorage());

  const update = useCallback((patch: Partial<IStoredPeriod>): void => {
    setData((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // errors
      }
      window.dispatchEvent(
        new CustomEvent(CUSTOM_EVENT_NAME, { detail: next })
      );
      return next;
    });
  }, []);

  useEffect(() => {
    const onCustom = (e: Event): void => {
      try {
        const payload = (e as CustomEvent).detail as IStoredPeriod | undefined;
        const next = payload ?? readStorage();
        setData(next);
      } catch {
        setData(readStorage());
      }
    };
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== STORAGE_KEY) return;
      setData(readStorage());
    };
    window.addEventListener(CUSTOM_EVENT_NAME, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CUSTOM_EVENT_NAME, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setStartDate = useCallback(
    (v: string | null) => update({ startDate: v }),
    [update]
  );
  const setEndDate = useCallback(
    (v: string | null) => update({ endDate: v }),
    [update]
  );
  const setPeriodType = useCallback(
    (v: typeof PRESELECTED_RANGE[keyof typeof PRESELECTED_RANGE] | null) =>
      update({ periodType: v }),
    [update]
  );
  const setOffset = useCallback((v: number) => update({ offset: v }), [update]);

  return {
    ...data,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  };
};
